import React, { useState, useEffect, useCallback } from 'react';
import { message } from 'antd';
import { useMutation } from '@apollo/client';
import dayjs, { Dayjs } from 'dayjs';
import { t } from 'i18n';
import { useStepDetailRender, formatTimeDisplay, type OptionalString } from './hooks/useStepDetailRender';
import { useTaskCommon } from '../../hooks/useTaskCommon';
import EditToolbar, { useEditToolbar } from '../EditToolbar';
import { useWarehousePermissionGuardedStartEdit } from '../../hooks/useWarehousePermissionGuardedStartEdit';
import { GET_TMS_TASK_DETAIL } from '../../graphql/queries/getTmsTaskDetail';
import type { TmsTaskWaypointType, TmsTaskDetailType } from '../../graphql/queries/getTmsTaskDetail';
import {
    UpdateTmsTaskWaypointToDepartureInput,
    UPDATE_TMS_TASK_WAYPOINT_TO_DEPARTURE_FOR_PORTAL,
    ApiResult,
} from '../../graphql/mutations/taskMutations';
import { parseResultCode } from '../../../../../utils/resultCode';
import { getUserInfo } from '../../../../../utils/auth';
import { translateBackendError } from './utils/errorMessage';

interface DepartureDetailProps {
    waypoint: TmsTaskWaypointType;
    stepDetailAllowEdit: boolean;
    task?: TmsTaskDetailType;
}

const DepartureDetail: React.FC<DepartureDetailProps> = ({ waypoint, stepDetailAllowEdit, task }) => {
    const [plannedDepartureTime, setPlannedDepartureTime] = useState<OptionalString>(formatTimeDisplay(waypoint.planDepartureAt));
    const [plannedDepartureTimezone] = useState<OptionalString>(waypoint.planDepartureTimezone);
    const [actualDepartureTime, setActualDepartureTime] = useState<OptionalString>(formatTimeDisplay(waypoint.actualDepartureAt));
    const [actualDepartureTimezone] = useState<OptionalString>(waypoint.actualDepartureTimezone);
    const [departureSealPhotoUrls, setDepartureSealPhotoUrls] = useState<string[]>(waypoint.departureSealPhotosUrlsJson || []);
    const [departureBolPhotoUrls, setDepartureBolPhotoUrls] = useState<string[]>(waypoint.departurePhotosUrlsJson || []);

    const [actualDraft, setActualDraft] = useState<Dayjs | null>(waypoint.actualDepartureAt ? dayjs(waypoint.actualDepartureAt) : null);
    const [departureSealPhotosDraft, setDepartureSealPhotosDraft] = useState<string[]>(waypoint.departureSealPhotosJson || []);
    const [departureBolPhotosDraft, setDepartureBolPhotosDraft] = useState<string[]>(waypoint.departurePhotosJson || []);
    const [remarkDraft, setRemarkDraft] = useState<string>(waypoint.departureRemark || '');

    const { isEditing, startEdit, handleCancel, exitEdit } = useEditToolbar({
        onStartEdit: useCallback(() => {
            setActualDraft(waypoint.actualDepartureAt ? dayjs(waypoint.actualDepartureAt) : null);
            setDepartureSealPhotosDraft(waypoint.departureSealPhotosJson || []);
            setDepartureBolPhotosDraft(waypoint.departurePhotosJson || []);
            setRemarkDraft(waypoint.departureRemark || '');
        }, [waypoint.actualDepartureAt, waypoint.departureSealPhotosJson, waypoint.departurePhotosJson, waypoint.departureRemark]),
    });
    const guardedStartEdit = useWarehousePermissionGuardedStartEdit(waypoint.waypointId, startEdit);

    const [updateDepartureMutation] = useMutation<
        { updateTmsTaskWaypointToDepartureForPortal: ApiResult },
        { input: UpdateTmsTaskWaypointToDepartureInput }
    >(UPDATE_TMS_TASK_WAYPOINT_TO_DEPARTURE_FOR_PORTAL);

    const plannedDepartureTimeLabel = t('tms.task.drawer.plannedDepartureTime');
    const actualDepartureTimeLabel = t('tms.task.drawer.actualDepartureTime');
    const sealPhotosLabel = t('tms.task.drawer.departureSealPhotos');
    const bolPhotosLabel = t('tms.task.drawer.bolPhotos');
    const remarkLabel = t('common.remark');

    const { compareTimeColor } = useTaskCommon();
    const { renderTimeRow, renderPhotoRow, renderRemarkRow } = useStepDetailRender({ isEditing });
    const actualDepartureColor = compareTimeColor(waypoint.planDepartureAt, waypoint.actualDepartureAt);

    useEffect(() => {
        setPlannedDepartureTime(formatTimeDisplay(waypoint.planDepartureAt));
        setActualDepartureTime(formatTimeDisplay(waypoint.actualDepartureAt));
        setActualDraft(waypoint.actualDepartureAt ? dayjs(waypoint.actualDepartureAt) : null);
    }, [waypoint.planDepartureAt, waypoint.actualDepartureAt]);

    useEffect(() => {
        setDepartureSealPhotoUrls(waypoint.departureSealPhotosUrlsJson || []);
    }, [waypoint.departureSealPhotosUrlsJson]);

    useEffect(() => {
        setDepartureBolPhotoUrls(waypoint.departurePhotosUrlsJson || []);
    }, [waypoint.departurePhotosUrlsJson]);

    useEffect(() => {
        if (!isEditing) {
            setDepartureSealPhotosDraft(waypoint.departureSealPhotosJson || []);
            setDepartureBolPhotosDraft(waypoint.departurePhotosJson || []);
            setRemarkDraft(waypoint.departureRemark || '');
        }
    }, [waypoint.departureSealPhotosJson, waypoint.departurePhotosJson, waypoint.departureRemark, isEditing]);

    const validateSave = (): boolean => {
        if (!actualDraft) {
            message.error(t('tms.task.drawer.actualDepartureTimeRequired'));
            return false;
        }

        if (departureSealPhotosDraft.length === 0) {
            message.error(t('tms.task.drawer.departureSealPhotosRequired'));
            return false;
        }

        if (departureBolPhotosDraft.length === 0) {
            message.error(t('tms.task.drawer.bolPhotosRequired'));
            return false;
        }

        return true;
    };

    const handleSave = async () => {
        if (!validateSave()) {
            return;
        }

        const formattedActual = actualDraft!.format('YYYY-MM-DD HH:mm');
        const accountId = getUserInfo()?.account?.id ? Number(getUserInfo()!.account.id) : undefined;

        const refetch = { query: GET_TMS_TASK_DETAIL, variables: { taskId: String(waypoint.taskId) } };

        try {
            const { data } = await updateDepartureMutation({
                variables: {
                    input: {
                        taskId: waypoint.taskId,
                        tmsTaskWayPointId: Number(waypoint.id),
                        departurePhotosJson: departureBolPhotosDraft,
                        departureSealPhotosJson: departureSealPhotosDraft,
                        accountId,
                        actualDepartureLocalTime: formattedActual,
                        departureRemark: remarkDraft,
                    },
                },
                refetchQueries: [refetch],
            });

            const result = data?.updateTmsTaskWaypointToDepartureForPortal;
            if (result?.success) {
                setActualDepartureTime(formatTimeDisplay(formattedActual));
                message.success(t('tms.task.drawer.saveSuccess'));
                exitEdit();
            } else {
                message.error(parseResultCode(result?.code ?? '', result?.message) || t('tms.task.drawer.saveFailed'));
            }
        } catch (error: any) {
            console.error('Failed to save departure detail:', error);
            message.error(translateBackendError(error));
        }
    };

    return (
        <div className="relative border-gray-200">
            <EditToolbar
                isEditing={isEditing}
                allowEdit={stepDetailAllowEdit}
                onStartEdit={guardedStartEdit}
                onSave={handleSave}
                onCancel={handleCancel}
            />
            <div className="mt-1 space-y-3">
                {renderTimeRow(plannedDepartureTimeLabel, plannedDepartureTime, plannedDepartureTimezone, null, () => {}, false)}
                {renderTimeRow(actualDepartureTimeLabel, actualDepartureTime, actualDepartureTimezone, actualDraft, setActualDraft, true, waypoint.actualDepartureTimezone, actualDepartureColor)}
                {renderPhotoRow(sealPhotosLabel, departureSealPhotoUrls, departureSealPhotosDraft, setDepartureSealPhotosDraft, departureSealPhotoUrls)}
                {renderPhotoRow(bolPhotosLabel, departureBolPhotoUrls, departureBolPhotosDraft, setDepartureBolPhotosDraft, departureBolPhotoUrls)}
                {renderRemarkRow(remarkLabel, waypoint.departureRemark, remarkDraft, setRemarkDraft)}
            </div>
        </div>
    );
};

export default DepartureDetail;